import React from 'react';
import { motion } from 'framer-motion';
import './Hero.css';

const Hero = ({ onGetStarted }) => {
  return (
    <div className="hero-wrapper">
      {/* Navigation */}
      <motion.nav 
        className="navbar"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="nav-container">
          <div className="nav-logo">
            <img 
              src="/images/logo.png" 
              alt="Adityam URJA Logo" 
              className="logo-image"
            />
          </div>
          <div className="nav-links">
            <a href="#features" className="nav-link">Features</a>
            <a href="#how-it-works" className="nav-link">How It Works</a>
            <a href="#testimonials" className="nav-link">Success Stories</a>
            <a href="tel:9430847044" className="nav-phone">+91 9430847044</a>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <motion.h1
              className="hero-title"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              Go Solar with Just
              <span className="hero-highlight"> ₹1 Down</span>
            </motion.h1>

            <motion.p
              className="hero-description"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              Save over 90% on your electricity bills. Get a personalized solar plan in 2 minutes. 
              EMI less than your current bill. Government subsidy up to ₹78,000 included.
            </motion.p>

            <motion.div
              className="hero-cta-group"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <button className="btn-primary-large" onClick={onGetStarted}>
                Get Free Solar Plan
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" />
                </svg>
              </button>
              <button className="btn-secondary-large" onClick={() => window.location.href = 'tel:9430847044'}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
                Call Us Now
              </button>
            </motion.div>

            <motion.div
              className="hero-trust-indicators"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <div className="trust-item">
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>No hidden charges</span>
              </div>
              <div className="trust-item">
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Free maintenance for 1 year</span>
              </div>
              <div className="trust-item">
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>25-year warranty</span>
              </div>
            </motion.div>
          </div>

          <motion.div
            className="hero-visual"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            <div className="visual-card">
              <div className="card-header">
                <span className="card-title">Your Savings</span>
                <span className="card-badge success">Active</span>
              </div>
              <div className="card-amount">
                <span className="amount-value">₹2,750</span>
                <span className="amount-label">/month EMI</span>
                <span className="amount-kw">3 KW Solar System</span>
              </div>
              <div className="card-comparison">
                <div className="comparison-before">
                  <span className="label">Current Bill</span>
                  <span className="value">₹3,500</span>
                </div>
                <div className="comparison-arrow">→</div>
                <div className="comparison-after">
                  <span className="label">Your EMI</span>
                  <span className="value orange">₹2,750</span>
                </div>
              </div>
              <div className="card-info">
                You save ₹750 every month!
              </div>
            </div>

            <div className="visual-stats">
              <div className="stat-box">
                <div className="stat-value">₹78,000</div>
                <div className="stat-label">Govt Subsidy</div>
              </div>
              <div className="stat-box">
                <div className="stat-value">90%</div>
                <div className="stat-label">Bill Savings</div>
              </div>
              <div className="stat-box">
                <div className="stat-value">25+</div>
                <div className="stat-label">Year Warranty</div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Animated background elements */}
        <div className="hero-bg-pattern"></div>
      </section>
    </div>
  );
};

export default Hero;
